import type { QueryKey } from "@tanstack/react-query";

/**
 * The venue-scoped invalidation convention every staff surface follows (ADR-0012).
 *
 * Canonical key shape: `["venue", venueId, resource, ...]`. A feature task keys ALL its queries with
 * [venueQueryKey] and registers its event-type prefix here — nothing else. Live events are only
 * invalidation *hints* (never the source of truth): on receipt the matching query refetches through
 * its normal path. An unmapped event type returns `undefined`, so `useRealtimeInvalidation` falls
 * through to invalidate-all — the safe default (events are rare; a full refetch is correct if coarse).
 *
 * Because the provider is mounted once per `/v/:venueId` tab with `venueId` closed over, a mapped key
 * is always scoped to THIS venue: an event on venue A's stream can only ever touch `["venue", A, …]`,
 * never venue B's cache in another tab (features/realtime-sync.md: a device receives only the venue
 * in view).
 */
export function venueQueryKey(venueId: string, ...rest: ReadonlyArray<string | number>): QueryKey {
  return ["venue", venueId, ...rest];
}

/** DOMAIN.md §D.6 event-type prefix → the venue-scoped resource its events invalidate. */
const EVENT_RESOURCE: ReadonlyArray<{ prefix: string; resource: string }> = [
  { prefix: "booking.", resource: "bookings" },
  { prefix: "availability.", resource: "availability" },
  { prefix: "waitlist.", resource: "waitlist" },
  { prefix: "service.", resource: "services" },
  { prefix: "hours.", resource: "services" },
  { prefix: "floor.", resource: "floorplan" },
  { prefix: "table.", resource: "floorplan" },
  { prefix: "guest.", resource: "guests" },
  { prefix: "blacklist.", resource: "guests" },
  { prefix: "staff.", resource: "staff" },
  { prefix: "assignment.", resource: "staff" },
  { prefix: "media.", resource: "media" },
  { prefix: "site.", resource: "site" },
  { prefix: "abuse.", resource: "abuse-blocks" },
  // billing-and-plans (§C.3): a subscription transition (dunning past-due/suspend/reactivate) refreshes
  // the billing surface's subscription + banner. Keyed under `["venue", venueId, "billing"]`.
  { prefix: "billing.subscription.", resource: "billing" },
  // rwg-place-matching (§C.1): a confirmed match re-emits `venue.location_confirmed`, so the
  // place-match status refetches across devices. Specific prefix — other `venue.*` events fall
  // through to invalidate-all.
  { prefix: "venue.location_confirmed", resource: "place-match" },
  // deposits-and-prepayment (§C.3): a guest-payment charge/refund/forfeit refreshes the per-venue
  // reconciliation report. Keyed under `["venue", venueId, "deposits"]`.
  { prefix: "billing.guest_payment.", resource: "deposits" },
];

/**
 * Maps an event type to the venue-scoped query key to invalidate, or `undefined` for an unmapped
 * type (library invalidates all — the safe fallback). Plugged into `useRealtimeInvalidation`.
 */
export function resolveVenueQueryKey(venueId: string, eventType: string): QueryKey | undefined {
  const hit = EVENT_RESOURCE.find((e) => eventType.startsWith(e.prefix));
  return hit ? venueQueryKey(venueId, hit.resource) : undefined;
}
